"use client";

import { Skeleton } from "antd";
import classNames from "classnames";
import Image, { ImageProps } from "next/image";
import { useCallback, useState } from "react";

export interface ProgressiveImageProps extends ImageProps {
  containerClassName?: string;
  skeletonClassName?: string;
  skeletonTestId?: string;
  disableSkeleton?: boolean;
  onReady?: () => void;
}

export function ProgressiveImage(props: ProgressiveImageProps) {
  const {
    containerClassName,
    skeletonClassName,
    skeletonTestId,
    disableSkeleton = false,
    onReady,
    onError,
    className,
    ...imageProps
  } = props;

  const [isReady, setIsReady] = useState(false);
  const [isError, setIsError] = useState(false);

  const handleComplete = useCallback(() => {
    setIsReady(true);
    onReady?.();
  }, [onReady]);

  const handleError = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
      setIsReady(true);
      setIsError(true);
      onError?.(event);
    },
    [onError]
  );

  if (isError) {
    return (
      <div
        className={classNames(
          "relative flex items-center justify-center overflow-hidden bg-background-elevated",
          containerClassName
        )}
      >
        <Image
          src="/dark-grey-logo.png"
          alt={imageProps.alt}
          width={80}
          height={80}
          className="relative z-[1] object-contain opacity-30"
          draggable={false}
          unoptimized
        />
      </div>
    );
  }

  return (
    <div className={classNames("relative", containerClassName)}>
      {!disableSkeleton && !isReady && (
        <div
          data-testid={skeletonTestId}
          className={classNames(
            "absolute inset-0 z-10 flex h-full w-full items-center justify-center bg-background-elevated",
            skeletonClassName
          )}
        >
          <Skeleton.Node active className="h-full! w-full!" />
        </div>
      )}

      <Image
        {...imageProps}
        className={classNames(
          "transition-opacity duration-300",
          !disableSkeleton && !isReady ? "opacity-0" : "opacity-100",
          className
        )}
        alt={imageProps.alt}
        onLoad={handleComplete}
        onError={handleError}
        unoptimized
      />
    </div>
  );
}
